import {BaseElement, BASIS, Label, OLabelInit, OSkinableInit} from "back/commons/basis";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {IDomLogical, IRegPointer, REG} from 'lib/commons/registry';
import {IView, IViewsContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {DOM, INodeFilter, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {ILastDatasBuilder, JLastDatas} from "lib/commons/lastDatas";
import {GFX} from "lib/commons/utils/gfx";

/**
 *
 */
export interface ICloseable<R> {
	close(returnValue?: R): boolean
}

/**
 * Option d'initialisation de l'observation du resize
 */
export interface OResizeObserverInit {
	observeResize?: boolean;
}
/**
 * Interface pour les widgets de type tooltip, menu, dialogue, fenêtres flotantes...
 */
export interface IPopupable<R = {}> extends HTMLElement, ICloseable<R> {

	opened: boolean;

	/**
	 * Ouvre le popup.
	 * @param anchor Positionnement du popup. Pour certains types de popupable, l'anchor n'est pas exploité (dialog).
	 * @param uiContainer Noeud contexte à partir duquel l'insertion graphique doit être évaluée (gestion du z-index des différentes fenêtres).
	 *      Utile lors que l'affichage s'effectue dans le contexte d'un autre IPopupable (dialogue...) :
	 *      on doit retrouver ce IPopupable parent pour gérer les z-index correctement.
	 *      Il sert également à resituer le focus à la sortie du popup.
	 * @param logicalParent affecte la propriété this.logicalParent pendant l'ouverture du popup
	 *      Préserve la hiérarchie "logique" pour retrouver le registre courant, etc.
	 * @event c-show Lancer avant l'affichage de la popup, peut être annulé pour empêcher l'affichage
	 * @event c-shown Lancer lorsque la popup est affiché
	 * @return true si la popup a été effectivement ouvert, false si la popup est déjà ouvert ou si l'ouverture a échoué (menu vide par ex.).
	 */
	show(anchor: OPopupAnchor, uiContainer: Node, logicalParent?: Element): boolean;

	/**
	 * @event c-beforeclose {@link IPopupCloseEvent} Lancer avant la fermeture de la popup, ne peut être annulé pour empêcher la fermeture
	 * @event c-close {@link IPopupCloseEvent} Lancer après la fermeture de la fenêtre
	 * @return true Si la fenêtre a été effectivement fermée, false sinon : cloture refusée ou popup déjà fermée
	 */
	close(returnValue?: R): boolean;

	/**
	 * Tente de fermer ce dialogue si le contenu l'autorise.
	 *
	 * @event c-beforeclose {@link IPopupCloseEvent} Lancer avant la fermeture de la popup, peut être annulé pour empêcher la fermeture
	 * @event c-close {@link IPopupCloseEvent} Lancer après la fermeture de la fenêtre
	 */
	tryClose(returnValue?: R): Promise<boolean>;

	/** Promesse résolue à la prochaine fermeture de cette popup avec returnValue en paramètre. */
	onNextClose(): Promise<R>;

	/** Type de la popup */
	popupType: EPopupType;

	/** Parent de la popup */
	parentPopup: IPopupable;

	/** Container contenant le slot principal */
	contentRoot: Node;

	/** Element à utiliser pour le changement des coordonnées lors d'un déplacement du popup. */
	eltForMove: HTMLElement;

	/** Indique si la popup a été déplacée par l'utilisateur (@link WindowBar} */
	manuallyMoved: boolean;

	/** Indique si la popup a été redimensionnée par l'utilisateur (@link WindowResizer} */
	manuallyResized: boolean;

	/** Repositionne la popup par rapport à la dernière ancre utilisée ou recalculée si l'utilisateur l'a modifiée */
	updatePosition(): void

	/** Fige le resize intrinsèque (issu du contenu) de la popup pendant un traitement asynchrone. */
	internalResizeFreeze(delta: number): void
}

/** Interface pour les évènements c-close et c-beforeclose d'une popup */
export interface IPopupCloseEvent extends CustomEvent {
	detail: { returnValue: any };
}

export const IS_Popupable = ((n: any): n is IPopupable => n && 'onNextClose' in n) as INodeFilter<IPopupable>;

export const enum EPopupType {
	/**
	 * Menus, menus contextuels, sous-menus.
	 * Capture les events sur toute la surface, toute action sortant du
	 * menu (click) ferme le menu.
	 */
	menu,

	/**
	 * Dialogue modal. Capture les events sur toute la surface,
	 * toute action en dehors du dialogue est tuée.
	 */
	dialog,

	/**
	 * Fenêtre flottante
	 * Aucune capture sur la surface de la page, pas de gestion de focus, ni d'évènements de fermeture.
	 */
	floating,

	/**
	 * Tooltips.
	 * Aucune capture sur la surface de la page, pas de gestion de focus, ni d'évènements de fermeture, non fixé à la page
	 */
	tooltip
}

/**
 * Mixin pour qu'un élément implémente IPopupable pour les
 * menus, menus contextuels, dialogues, fenêtres flottantes et tooltips.
 */
export function MxPopupable<T extends Constructor<BaseElement>>(Base: T, type: EPopupType) {
	return class MxPopupable extends Base implements IPopupable, IDomLogical, IView, IViewsContainer, ILastDatasBuilder {
		popupType: EPopupType;
		parentPopup: IPopupable;
		contentRoot: HTMLElement;
		eltForMove: HTMLElement;
		manuallyMoved: boolean;
		manuallyResized: boolean;

		uiContainer: Node;
		logicalParent: Element;

		/** A la fermeture du popup, focus cet élement. */
		restoreFocus?: HTMLElement;

		/** Mémoire de l'élément qui sera focusé à la fermeture = this.restoreFocus déclaré OU l'élément actif courant avant ouverture du popup. */
		protected _restoreFocus?: HTMLElement;
		protected _focusReentrance: EventListenerOrEventListenerObject;
		protected _currentAnchor: OPopupAnchor;
		protected _bodySlot: HTMLSlotElement;
		protected _subPopupSlot: HTMLSlotElement;
		protected _externalResize: {
			proxy: EventListener,
			timeout: number,
			previousWidth: number,
			previousHeight: number
		};
		protected _internalResize: {
			observer: ResizeObserver,
			request: number,
			skipNextEvent: boolean,
			target: HTMLElement,
			freeze: number
		};

		protected _returnValue: any;

		protected _closeLstns: Dict<EventListener>;
		protected _waitingClose: ((value?: any) => void)[];


		get opened() {return this.hasAttribute('opened')}

		get view(): IView {return this.firstElementChild as IView}

		get windowBar(): WindowBar | null {return DOM.findNext(this.shadowRoot, this.shadowRoot, (n): n is WindowBar => n instanceof WindowBar)}

		/** Si true, la view contenue sera définitivement fermée libération des ressources). */
		usedOnce?: boolean;

		protected _initialize(init: IRegPointer<any> & OResizeObserverInit & OTitleBarPointer & OResizerPointer) {
			super._initialize(init);
			this.popupType = type;
			this.attachShadow(DOMSH.SHADOWDOM_INIT);

			const sr = this.shadowRoot;
			const reg = this.findReg(init);
			reg.installSkin('popupable', sr);
			this._initAndInstallSkin(this.localName, init);

			// focusStart, focusEnd permettent de verrouiller le focus dans le popup (excepté pour les tooltip)
			this.shadowRoot.appendChild(<button id="focusStart" onfocus={this.onFocusStart}/> as HTMLButtonElement);

			// Slot principal sur le contentRoot, cad la div de position ou le resizer
			this.contentRoot = this.eltForMove = sr.appendChild(<div id="content"></div>);
			if (init.resizer) this.contentRoot = this.contentRoot.appendChild(new WindowResizer().initialize(init.resizer));
			// Ajout de la barre de titre
			if (init.titleBar) {
				const tiInit: OWindowBarInit = typeof init.titleBar === "string" ? {barLabel: {label: init.titleBar}} : init.titleBar;
				const ti = this.contentRoot.appendChild(new WindowBar().initialize(tiInit));
				ti.slot = "title";
			}
			this._builBodySlot();
			this.shadowRoot.appendChild(<button id="focusEnd" onfocus={this.onFocusEnd}/>);
			this._subPopupSlot = this.shadowRoot.appendChild(<slot name="subPopup"/>) as HTMLSlotElement;

			// Initialisation de la structure pour gérer les redimensionnement externe
			this._externalResize = {
				proxy: this.onExternalResize.bind(this),
				previousWidth: 0,
				previousHeight: 0,
				timeout: 0
			};
			// Initialisation de la structure pour gérer les redimensionnement interne
			if (init.observeResize !== false && 'ResizeObserver' in window) {
				this._internalResize = {
					observer: new ResizeObserver(this.onInternalResize),
					skipNextEvent: true,
					request: 0,
					target: this.contentRoot,
					freeze: 0
				};
			}

			this.addEventListener('keydown', function (this: MxPopupable, ev: KeyboardEvent) {
				if (!this.onKeyDown(ev)) {
					//Comportement par défaut, on ferme le popup sur Escape.
					if (ev.key === 'Escape') {
						ev.preventDefault();
						ev.stopImmediatePropagation();
						if (this.popupType == EPopupType.tooltip) {
							let tooltip = this as IPopupable;
							// Fermeture de toute la hiéarchie de tooltip sur le Escape
							do {
								tooltip.tryClose();
								const parent = tooltip.parentElement;
								tooltip = IS_Popupable(parent) && parent.popupType === EPopupType.tooltip && parent;
							} while (tooltip);
						} else {
							this.tryClose();
						}
						let popup = this as IPopupable;
						do {
							popup.tryClose();
							// Fermeture de toute la hiéarchie de tooltip sur le Escape
							popup = IS_Popupable(popup.parentElement) && popup.parentElement.popupType === EPopupType.tooltip && popup.parentElement;
						} while (popup);
					}
				}
			});
		}

		protected _builBodySlot() {
			this._bodySlot = this.contentRoot.appendChild(<slot id="body"/>) as HTMLSlotElement;
		}

		show(anchor: OPopupAnchor, uiContainer: Node, logicalParent?: Element): boolean {
			if (this.opened) return false;
			this._returnValue = undefined;
			this._restoreFocus = this.restoreFocus || DOMSH.findDeepActiveElement(document) as HTMLElement;
			this.uiContainer = uiContainer;
			this.logicalParent = logicalParent !== undefined ? logicalParent : DOM.findLogicalParentOrSelf(uiContainer, null, DOM.IS_element);

			if (this.dispatchEvent(new CustomEvent('c-show', {bubbles: false, composed: false, cancelable: true})) === false) return false;

			this.internalResizeFreeze(1);
			this.parentPopup = findPopupableParent(uiContainer);
			if (this.parentPopup) {
				//popup dans un popup, on imbrique.
				this.remove();
				this.setAttribute('slot', 'subPopup');
				this.setAttribute('opened', 'sub');
				this.parentPopup.appendChild(this);
			} else {
				this.remove();
				this.setAttribute('opened', 'main');
				document.body.appendChild(this);
			}

			// Aria roles
			if (type === EPopupType.menu) this.setAttribute('role', 'menu');
			else if (type === EPopupType.dialog || type == EPopupType.floating) this.setAttribute('role', 'dialog');
			else if (type === EPopupType.tooltip) this.setAttribute('role', 'tooltip');

			this._closeLstns = {};

			if (type === EPopupType.menu || type == EPopupType.dialog) {
				if (!DOMSH.isAncestor(this._bodySlot, document.activeElement)) {
					// Focus sur le premier élément du menu ou du dialog
					const firstFocusable = DOMSH.findFlatNext(this._bodySlot, this.contentRoot, DOM.IS_focusable);
					if (firstFocusable) firstFocusable.focus();
					else {
						this.contentRoot.tabIndex = 0;
						this.contentRoot.focus();
					}
				}

				// Ajout des listeners de capture du scroll pour les dialog et le menu
				this.addEventListener('mousewheel', this.onScrollPrevent, {passive: false});
				this.addEventListener('touchmove', this.onScrollPrevent, {passive: false});
				this._closeLstns['mousewheel'] = this.onScrollPrevent;
				this._closeLstns['touchmove'] = this.onScrollPrevent;
			}

			if (type === EPopupType.dialog) {
				// Permet de replacer le focus dans la dialogue si celui-ci était sorti de la page
				this._focusReentrance = () => {
					let subDialog = false;
					for (const subPopup of this._subPopupSlot.assignedNodes()) {
						if ((subPopup as IPopupable).popupType == EPopupType.dialog) {
							subDialog = true;
							break;
						}
					}
					if (!subDialog) this.contentRoot.focus();
				};
				window.addEventListener('focus', this._focusReentrance);
			}

			// Listeners de fermeture du menu
			if (type === EPopupType.menu) {
				this.addEventListener('pointerdown', this.menuOnPointerDown);
				this.addEventListener("contextmenu", this.menuOnContextMenu);
				this._closeLstns['pointerdown'] = this.menuOnPointerDown;
				this._closeLstns['contextmenu'] = this.menuOnContextMenu;
				this._currentAnchor = anchor;
			} else if (type === EPopupType.dialog) {
				this._currentAnchor = anchor || {viewPortY: 'middle', fixSize: true};
			} else if (type === EPopupType.floating || type === EPopupType.tooltip) {
				this._currentAnchor = anchor;
			}


			this._externalResize.previousWidth = window.innerWidth;
			this._externalResize.previousHeight = window.innerHeight;
			window.addEventListener('resize', this._externalResize.proxy);

			this._setPosition(this._currentAnchor);
			this.internalResizeFreeze(-1);

			this.onViewShown();
			return true;
		}

		protected closing?: boolean;

		close(returnValue?: any): boolean {
			if (!this.opened || this.closing) return false;
			this._returnValue = returnValue;
			this.closing = true; //reentrancy guard
			this.dispatchEvent(new CustomEvent('c-beforeclose', {
				bubbles: false,
				composed: false,
				cancelable: false,
				detail: {returnValue: this._returnValue}
			}));
			this.closing = false;
			for (const eventType in this._closeLstns) this.removeEventListener(eventType, this._closeLstns[eventType]);
			if (this._focusReentrance) window.removeEventListener('focus', this._focusReentrance);
			window.removeEventListener('resize', this._externalResize.proxy);
			clearTimeout(this._externalResize.timeout);
			if (this._internalResize) this._internalResize.observer.disconnect();
			this.onViewHidden(this.usedOnce);
			return true;
		}

		async tryClose(returnValue?: any): Promise<boolean> {
			if (!this.opened) return false;
			this._returnValue = returnValue;
			if (await VIEWS.canHideView(this, true)) {
				return this.close(returnValue);
			}
			return false;
		}

		onNextClose(): Promise<any> {
			return new Promise((resolve: (value?: any) => void) => {
				if (!this._waitingClose) this._waitingClose = [resolve];
				else this._waitingClose.push(resolve);
			});
		}

		onViewShown() {
			this.dispatchEvent(new CustomEvent('c-shown', {bubbles: false, composed: false}));
			VIEWS.onViewShown(this.view);
		}

		onViewBeforeHide(close?: boolean): boolean {
			for (const subPopup of this._subPopupSlot.assignedNodes() as IView[]) {
				if ('onViewBeforeHide' in subPopup && !subPopup.onViewBeforeHide(close)) return false;
			}
			if (this.view && 'onViewBeforeHide' in this.view && !this.view.onViewBeforeHide(close)) return false;

			if (close) {
				const beforeCloseEvent = new CustomEvent('c-beforeclose', {
					bubbles: false,
					composed: false,
					cancelable: true,
					detail: {returnValue: this._returnValue}
				});
				if (this.dispatchEvent(beforeCloseEvent) === false) return false;
			}
			return true;
		}

		async onViewWaitForHide(close?: boolean): Promise<boolean> {
			for (const subPopup of this._subPopupSlot.assignedNodes() as IView[]) {
				if ('onViewWaitForHide' in subPopup && !await subPopup.onViewWaitForHide(close)) return false;
			}
			if (this.view && 'onViewWaitForHide' in this.view && !await this.view.onViewWaitForHide(close)) return false;
			// Pas d'attente pour la popup proprement dite
			return true;
		}

		/** Replace le popup dans son contexte d'origine et dispatch les evts de la fermeture. */
		onViewHidden(closed?: boolean) {
			for (const subPopup of this._subPopupSlot.assignedNodes() as IView[]) {
				VIEWS.onViewHidden(subPopup, closed);
			}
			VIEWS.onViewHidden(this.view, closed);

			// Pas d'attente pour la popup proprement dite
			this.removeAttribute('opened');

			// On restore le focus que si le popup détient le toujours (ie une action ne l'a pas volotairement placé en dehors).
			if (this._restoreFocus && this.contentRoot && this.matches(":focus-within") && this._restoreFocus.isConnected) {
				this._restoreFocus.focus();
				this._restoreFocus = null;
			}

			if (this.parentNode) this.remove();

			if (this._waitingClose) {
				for (const fct of this._waitingClose) fct(this._returnValue);
				this._waitingClose = null;
			}
			this.dispatchEvent(new CustomEvent('c-close', {bubbles: true, composed: false, detail: {returnValue: this._returnValue}}));

			delete this.logicalParent; //il sera réaffecté au prochain appel show().
		}

		visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
			if (options && options.visible && !this.opened) return;
			let result;
			if (this.view) result = visitor(this.view);
			if (result !== undefined) return result;
			for (const subPopup of this._subPopupSlot.assignedNodes() as IView[]) {
				result = visitor(subPopup);
				if (result !== undefined) return result;
			}
		}

		async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
			if (options && options.visible && !this.opened) return;
			let result;
			if (this.view) result = await visitor(this.view);
			if (result !== undefined) return result;
			for (const subPopup of this._subPopupSlot.assignedNodes() as IView[]) {
				result = await visitor(subPopup);
				if (result !== undefined) return result;
			}
		}

		/**
		 * On tue le parcours de la hiérarchie à la racine d'un popupable. C'est le contexte
		 * suscpetible de réouvrir ce popupable qui doit appeler LASTDATAS.buildLastDatas(..., popupable)
		 */
		buildLastDatas(parentLastDatas: JLastDatas): void {}

		/**
		 * Pour EPopupType.menu, permet de surcharger le comportement du popup sur un KeyboardEvent.
		 * @return true si l'event a été pris en charge.
		 */
		protected onKeyDown(ev: KeyboardEvent): boolean {return false}


		/** Pour EPopupType.menu, listener qui ferme la popup ou le menu racine sur un clic en dehors du contentRoot */
		protected menuOnPointerDown(ev: PointerEvent) {
			if (ev.button != 2 && ev.composedPath()[0] === this) {
				ev.preventDefault();
				let rootMenu: IPopupable = this;
				while (rootMenu.parentPopup && rootMenu.parentPopup.popupType == EPopupType.menu) rootMenu = rootMenu.parentPopup;
				rootMenu.tryClose();
			}
		}

		/** Pour EPopupType.menu, listener qui ferme la popup ou le menu racine sur un contextmenu en dehors du contentRoot */
		protected menuOnContextMenu(ev: MouseEvent) {
			if (ev.composedPath()[0] === this) {
				ev.stopPropagation();
				ev.preventDefault();
				let rootMenu: IPopupable = this;
				while (rootMenu.parentPopup && rootMenu.parentPopup.popupType == EPopupType.menu) rootMenu = rootMenu.parentPopup;
				rootMenu.tryClose();
			}
		}

		/** Pour EPopupType.menu, listener qui bloque le scroll lorsque le menu est ouvert */
		protected onScrollPrevent(ev: MouseEvent) {
			if (ev.composedPath()[0] == this) ev.preventDefault();
		}

		protected _setPosition(anchor: OPopupAnchor) {
			this.internalResizeFreeze(1);
			const st = this.eltForMove.style;
			// on le force au début en haut à gauche pour éviter tout effet de pre-sizing lié à sa
			// position relative naturelle et les limites du viewport qui peuvent réduire sa hauteur ou largeur.
			st.top = '0';
			st.left = '0';
			st.width = (anchor as OInitSize).initWidth || null;
			st.height = (anchor as OInitSize).initHeight || null;
			st.maxHeight = (anchor as OInitSize).initMaxHeight || null;
			st.maxWidth = null;
			let {width, height} = this.eltForMove.getBoundingClientRect();

			if ((anchor as OInitSize).fixSize) {
				const padding = (anchor as OViewPortPosition).viewPortPadding || 10;
				const maxW = document.documentElement.clientWidth - padding * 2;
				if (width > maxW) {
					width = maxW;
					st.width = width + 'px';
					height = this.eltForMove.offsetHeight;
				} else st.width = width + 'px';
				st.height = Math.min(document.documentElement.clientHeight - padding * 2, height) + 'px';
			}

			const aroundElem = isAroundPosition(anchor) ? anchor.elem : (anchor instanceof HTMLElement ? anchor : null);
			if (aroundElem && aroundElem !== this.eltForMove /*anchor === node => anchor props portés par le popup (notif) */) {
				this.fixAroundPos(aroundElem, st, width, height);
			} else if (isFixedPosition(anchor)) {
				if (anchor.initX + width <= document.documentElement.clientWidth) st.left = anchor.initX + 'px';
				else st.left = Math.max(0, document.documentElement.clientWidth - width) + 'px';
				if (anchor.initY + height <= document.documentElement.clientHeight) st.top = anchor.initY + 'px';
				else st.top = Math.max(0, document.documentElement.clientHeight - height) + 'px';
			} else if (isRelativePosition(anchor)) {
				this.fixRelativePos(anchor, this.eltForMove, width, height);
			} else {
				this.fixScreenPos(anchor as OViewPortPosition, this.eltForMove, width, height);
			}

			if (!(anchor as OInitSize).fixSize) {
				//On impose les maxHeight et maxWidth pour restreindre node
				st.maxWidth = (document.documentElement.clientWidth - parseInt(st.left.substr(0, st.left.length - 2))) + 'px';
				if (!(anchor as OInitSize).initMaxHeight) {
					st.maxHeight = (document.documentElement.clientHeight - parseInt(st.top.substr(0, st.top.length - 2))) + 'px';
				}
			}
			this._currentAnchor = anchor;
			this.internalResizeFreeze(-1);
		}

		updatePosition() {
			if ((this.manuallyMoved || this.manuallyResized)) {
				const style = getComputedStyle(this.eltForMove);
				let anchor: Exclude<OPopupAnchor, HTMLElement>;

				if (this.manuallyMoved) {
					anchor = {
						initX: parseFloat(style.left) * window.innerWidth / this._externalResize.previousWidth,
						initY: parseFloat(style.top) * window.innerHeight / this._externalResize.previousHeight,
						initWidth: (this._currentAnchor as OInitSize).initWidth,
						initHeight: (this._currentAnchor as OInitSize).initHeight,
						fixSize: (this._currentAnchor as OInitSize).fixSize,
					}
				} else {
					if (this._currentAnchor instanceof HTMLElement) {
						anchor = {
							initX: parseFloat(style.left),
							initY: parseFloat(style.top),
							initWidth: null,
							initHeight: null,
							fixSize: false
						}
					} else {
						anchor = Object.assign({}, this._currentAnchor);
					}
				}
				if (this.manuallyResized) {
					anchor.fixSize = true;
					anchor.initWidth = parseFloat(style.width) * window.innerWidth / this._externalResize.previousWidth + 'px';
					anchor.initHeight = (parseFloat(style.height) * window.innerHeight / this._externalResize.previousHeight) + 'px';
				}
				this._setPosition(anchor);
			} else {
				this._setPosition(this._currentAnchor);
			}
		}

		protected fixAroundPos(anchor: Element | ClientRect, st: CSSStyleDeclaration, width: number, height: number) {
			const anchorRect = 'left' in anchor ? anchor : anchor.getBoundingClientRect();
			if (anchorRect.left + width <= document.documentElement.clientWidth) st.left = Math.round(anchorRect.left) + 'px';
			else st.left = Math.max(0, Math.round(anchorRect.right - width)) + 'px';
			if (anchorRect.bottom + height <= document.documentElement.clientHeight) st.top = Math.round(anchorRect.bottom) + 'px';
			else st.top = Math.max(0, Math.round(anchorRect.top - height)) + 'px';
		}

		protected fixRelativePos(pos: ORelativePosition, target: HTMLElement, targetW: number, targetH: number): boolean {
			let fromRect = pos.posFrom.getBoundingClientRect();
			if (pos.intersectWith) fromRect = GFX.intersect(fromRect, pos.intersectWith.getBoundingClientRect()) || pos.intersectWith.getBoundingClientRect();
			const dir = window.getComputedStyle(pos.posFrom).direction || 'ltr';

			let x;
			switch (pos.fromX) {
			case 'end':
				x = dir === 'ltr' ? fromRect.right : fromRect.left;
				break;
			case 'middle':
				x = fromRect.left + (fromRect.right - fromRect.left) / 2;
				break;
			default:
				x = dir === 'ltr' ? fromRect.left : fromRect.right;
			}
			if (pos.marginX) x += dir === 'ltr' ? pos.marginX : -pos.marginX;
			switch (pos.targetX) {
			case 'end':
				if (dir === 'ltr') x -= targetW;
				break;
			case 'middle':
				x += (dir === 'ltr') ? -targetW / 2 : targetW / 2;
				break;
			default:
				if (dir === 'rtl') x -= targetW;
			}

			const tolX = pos.toleranceX || 0;
			const winX = document.documentElement.clientWidth - targetW;
			if (x >= -tolX && x <= winX + tolX) {
				let y;
				switch (pos.fromY) {
				case 'bottom':
					y = fromRect.bottom;
					break;
				case 'middle':
					y = fromRect.top + (fromRect.bottom - fromRect.top) / 2;
					break;
				default:
					y = fromRect.top;
				}
				if (pos.marginY) y += pos.marginY;
				switch (pos.targetY) {
				case 'bottom':
					y -= targetH;
					break;
				case 'middle':
					y -= targetH / 2;
					break;
				}
				const tolY = pos.toleranceY || 0;
				const winY = document.documentElement.clientHeight - targetH;
				if (y >= -tolY && y <= winY + tolY) {
					target.style.top = Math.max(0, Math.min(winY, y)) + 'px';
					target.style.left = Math.max(0, Math.min(winX, x)) + 'px';
					return true;
				}
			}
			if (pos.notAvailableSpace) {
				if (isRelativePosition(pos.notAvailableSpace)) return this.fixRelativePos(pos.notAvailableSpace, target, targetW, targetH);
				this.fixScreenPos(pos.notAvailableSpace, target, targetW, targetH);
			} else {
				this.fixAroundPos(pos.posFrom, target.style, targetW, targetH);
			}
			return true;
		}

		protected fixScreenPos(anchor: OViewPortPosition, target: HTMLElement, targetW: number, targetH: number) {
			const dir = window.getComputedStyle(target).direction || 'ltr';
			const padding = anchor.viewPortPadding || 10;
			const winW = document.documentElement.clientWidth;
			const winH = document.documentElement.clientHeight;
			const maxWinW = winW - padding - padding;
			const maxWinH = winH - padding - padding;
			if (targetW > maxWinW || targetH > maxWinH) {
				//on restreint les dimensions
				const originalW = target.style.maxWidth;
				const originalH = target.style.maxHeight;
				if (targetW > maxWinW) target.style.maxWidth = maxWinW + 'px';
				if (targetH > maxWinH) target.style.maxHeight = maxWinH + 'px';
				targetW = target.offsetWidth;
				targetH = target.offsetHeight;
				const resetMaxSize = () => {
					target.style.maxWidth = originalW;
					target.style.maxHeight = originalH;
					this.removeEventListener('c-close', resetMaxSize);
				};
				this.addEventListener('c-close', resetMaxSize);
			}
			let x: number;
			let y: number;
			if (anchor.viewPortX === 'start') x = dir === 'ltr' ? padding : winW - targetW - padding;
			else if (anchor.viewPortX === 'end') x = dir === 'ltr' ? winW - targetW - padding : padding;
			else x = (winW - targetW) / 2;
			if (anchor.viewPortY === 'bottom') y = winH - targetH - padding;
			else if (anchor.viewPortY === '1/3') y = (winH - targetH) / 3;
			else if (anchor.viewPortY === 'middle') y = (winH - targetH) / 2;
			else y = padding;
			target.style.top = y + 'px';
			target.style.left = x + 'px';
		}

		onFocusStart(this: Button): void {
			let newFocus;
			const popupable = DOMSH.findHost<MxPopupable>(this);
			if (type === EPopupType.tooltip) {
				newFocus = DOM.IS_focusable(popupable.uiContainer) ? popupable.uiContainer : DOMSH.findFlatPrevious(popupable.uiContainer, document, DOM.IS_focusable);
				popupable.tryClose();
			} else {
				newFocus = DOMSH.findFlatLastDesc(popupable.contentRoot.lastChild, DOM.IS_focusable);
			}
			if (newFocus) newFocus.focus();
		}

		onFocusEnd(this: Button): void {
			let newFocus;
			const popupable = DOMSH.findHost<MxPopupable>(this);
			if (type === EPopupType.tooltip) {
				if(!DEBUG_TOOLTIP) {
					newFocus = DOMSH.findFlatNext(popupable.uiContainer, document, DOM.IS_focusable);
					popupable.tryClose();
				}
			} else {
				newFocus = DOMSH.findFlatNext(popupable.contentRoot, popupable.contentRoot, DOM.IS_focusable);
			}
			if (newFocus) newFocus.focus();
		}

		onExternalResize(this: MxPopupable, ev: UIEvent) {
			if (this._externalResize.timeout !== null) window.cancelIdleCallback(this._externalResize.timeout);
			this._externalResize.timeout = window.requestIdleCallback(() => {
				this._externalResize.timeout = null;
				this.updatePosition();
				this._externalResize.previousWidth = window.innerWidth;
				this._externalResize.previousHeight = window.innerHeight;
			});
		}

		onInternalResize(this: void, entries: ResizeObserverEntry[]): void {
			const popupable = findPopupableParent(entries[0].target) as MxPopupable;
			if (popupable._internalResize.skipNextEvent) {
				popupable._internalResize.skipNextEvent = false;
				return;
			}

			if (popupable._internalResize.request) return;
			if (popupable.manuallyResized) {
				popupable._internalResize.observer.unobserve(popupable._internalResize.target);
				return;
			}

			popupable._internalResize.request = window.requestAnimationFrame(() => {
				popupable._internalResize.request = 0;
				popupable.updatePosition();
			});
		}

		internalResizeFreeze(delta: number) {
			if (this._internalResize) {
				this._internalResize.freeze += delta;
				if (this._internalResize.freeze === 1) this._internalResize.observer.unobserve(this._internalResize.target)
				else if (this._internalResize.freeze === 0) {
					this._internalResize.skipNextEvent = true;
					this._internalResize.observer.observe(this._internalResize.target);
				}
			}
		}
	}
}

REG.reg.registerSkin('popupable', 1, /* language=CSS */ `
	:host {
		display: none;
	}

	:host([opened]) {
		display: block;
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 1001;
		pointer-events: none;
	}

	#content {
		display: flex;
		position: fixed;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		box-sizing: border-box;
		pointer-events: auto;
	}

	#focusStart, #focusEnd {
		position: absolute;
		border: 0;
		padding: 0;
	}
`);


export interface OTitleBarPointer {
	titleBar?: OWindowBarInit | string;
}

export interface OResizerPointer {
	resizer?: OWindowResizerInit
}

export type OPopupAnchor =
/** Positionne le popup au dessus ou en dessous de cet élément, aligné à gauche ou à droite en fonction l'espace dispo. */
	HTMLElement
	/** Similaire au positionnement par élément mais avec les options d'initialisation de taille */
	| OAroundPosition
	/** Coordonnées fixées, mais contraintes par la taille de la window. */
	| OFixedPosition
	/** Position relative avancée. */
	| ORelativePosition
	/** Position relative au viewport. */
	| OViewPortPosition;


export interface OInitSize {
	/** Largeur à utiliser pour initialiser le popup. */
	initWidth?: string
	/** Hauteur à utiliser pour initialiser le popup. */
	initHeight?: string

	/**
	 * Si activé, ajoute les propriétés width / height après l'initialisation du popup.
	 *
	 * Nécessaire si le contenu contient une zone de scroll en flex=1.
	 * Inconvénient: ne s'adapte plus dynamiquement en fonction des changements du contenu.
	 *
	 * Les dialogs et floatting windows sont en fixSize=true par défaut,
	 * => ajouter explicitement fixSize=false si le dialogue ne s'ajuste pas à la hauteur (ie pas de flex=1)
	 */
	fixSize?: boolean

	/** Si fixSize=false, spécifie un min au max-height du popup.*/
	initMaxHeight?: string
}

export interface OAroundPosition extends OInitSize {
	elem: HTMLElement | ClientRect
}

function isAroundPosition(anchor: any): anchor is OAroundPosition { return 'elem' in anchor }

export interface OFixedPosition extends OInitSize {
	initX: number
	initY: number
}

function isFixedPosition(anchor: any): anchor is OFixedPosition {return 'initX' in anchor}

export interface ORelativePosition extends OInitSize {
	posFrom: Element
	/** Intersection du rect de posFrom avec intersectWith. */
	intersectWith?: Element
	fromX?: 'start' /* default */ | 'middle' | 'end'
	targetX?: 'start' /* default */ | 'middle' | 'end'
	/* Distance en pixel entre les deux lignes. si >0 target est plus à droite de posFrom en LTR, l'inverse en RTL. */
	marginX?: number
	/* Variation autorisée de la margin en pixels pour trouver l'espace nécessaire. Toujours >0.*/
	toleranceX?: number
	fromY?: 'top' /* default */ | 'middle' | 'bottom'
	targetY?: 'top' /* default */ | 'middle' | 'bottom'
	marginY?: number
	toleranceY?: number
	notAvailableSpace?: ORelativePosition | OViewPortPosition
}

function isRelativePosition(anchor: any): anchor is ORelativePosition {return anchor.posFrom}

export interface OViewPortPosition extends OInitSize {
	viewPortX?: 'start' | 'middle' /* default */ | 'end'
	viewPortY?: 'top' /* default */ | '1/3' | 'middle' | 'bottom'
	viewPortPadding?: number //10 par défaut, pour start, end, top, bottom
}

export interface WindowBar extends OWindowBarInit {
	initialize(init: OWindowBarInit): this;
}

export interface OWindowBarInit extends OSkinableInit {
	closeButton?: OButtonInit;
	barLabel?: OLabelInit | (() => HTMLElement);
	noDrag?: boolean;
}

export class WindowBar extends BaseElement {
	closeButton?: Button;
	barLabel?: Label;

	get noDrag(): boolean {return this.getAttribute('draggable') !== 'true'}

	protected _initialize(init: OWindowBarInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const reg = this.findReg(init);
		if (typeof init.barLabel === "function") {
			sr.appendChild(init.barLabel());
		} else {
			let initLabel: OLabelInit;
			if (init.barLabel) {
				initLabel = init.barLabel;
				if (!('noShadow' in initLabel)) initLabel.noShadow = true;
				if (!('ellipsis' in initLabel)) initLabel.ellipsis = true;
			} else {
				initLabel = {noShadow: true, ellipsis: true};
			}
			initLabel.reg = reg;
			this.barLabel = sr.appendChild(new Label().initialize(initLabel));
		}
		if (init.closeButton !== null) {
			const initBtn = BASIS.newInit(init.closeButton, reg);
			if (!initBtn.uiContext) initBtn.uiContext = 'custom';
			if (!('icon' in initBtn)) initBtn.icon = '/@skin@/commons/widgets/popupable/close.svg';
			if (!initBtn.title) initBtn.title = 'Fermer';
			this.closeButton = sr.appendChild(new Button().initialize(initBtn));
			this.closeButton.onclick = (ev: UIEvent) => {findPopupableParent(this).tryClose()};
		}
		this._initAndInstallSkin('c-window-bar', init);
		if (!init.noDrag) this.setAttribute('draggable', 'true');
		if (!this.noDrag) {
			this.ondragstart = windowDragStart;
			this.ondragover = defaulDragHover;
		}
	}
}

REG.reg.registerSkin('c-window-bar', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: min-content;
		min-width: 0;
		align-items: center;
		color: var(--dialog-color);
		background-color: var(--dialog-bgcolor);
		--pressed-bgcolor: var(--inv-pressed-bgcolor);
		--filter: var(--inv-filter);
		--hover-filter: var(--inv-hover-filter);
		--active-filter: var(--inv-active-filter);
		padding: 0.2em 0.5em;
	}

	c-label {
		flex: 1;
		user-select: none;
		font-size: 1.1em;
		width: min-content;
		overflow: hidden;
		display: -webkit-box;
	  -webkit-line-clamp: 2;
	  -webkit-box-orient: vertical;
	}

	c-button {
		margin-inline-start: .5em;
	}

	.icon {
		background: var(--dialog-color) var(--icon-img) no-repeat center / contain;
	}
`);

customElements.define('c-window-bar', WindowBar);


function windowDragStart(this: WindowBar, ev: DragEvent) {
	const popupable = findPopupableParent(this);
	popupable.manuallyMoved = true;
	const toMove = popupable.eltForMove;
	const rect = toMove.getBoundingClientRect();
	//On fige :
	const st = toMove.style;
	st.margin = "unset"; //Peut perturber (margin: auto dans dialog).
	st.left = rect.left + 'px';
	st.top = rect.top + 'px';
	st.width = rect.width + 'px';
	st.height = rect.height + 'px';
	st.right = null;
	st.bottom = null;
	ev.dataTransfer.effectAllowed = 'move';
	ev.dataTransfer.setDragImage(<span/>, 0, 0);
	const baseLeft = rect.left - ev.screenX;
	const baseTop = rect.top - ev.screenY;
	this.ondrag = (ev: DragEvent) => {
		if (ev.clientX === 0) return;//bug chrome63 : au drop un event 'drag' avec tout à 0 est dispatché.
		toMove.style.left = (baseLeft + ev.screenX) + 'px';
		toMove.style.top = (baseTop + ev.screenY) + 'px';
	};
}

function defaulDragHover(ev: DragEvent) {
	ev.dataTransfer.dropEffect = 'move';
	ev.preventDefault();
}


export interface OWindowResizerInit extends OSkinableInit {}


export interface WindowResizer {
	initialize(init?: OWindowResizerInit): this;
}

export class WindowResizer extends BaseElement {
	protected _initialize(init: OSkinableInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.addHandler("tl", sr);
		this.addHandler("t", sr);
		this.addHandler("tr", sr);
		this.addHandler("l", sr);
		sr.appendChild(<slot/>);
		this.addHandler("r", sr);
		this.addHandler("l", sr);
		sr.appendChild(<slot name="title"/>);
		this.addHandler("r", sr);
		this.addHandler("bl", sr);
		this.addHandler("b", sr);
		this.addHandler("br", sr);
	}

	protected addHandler(pos: string, sr: ShadowRoot) {
		const div = sr.appendChild(<div id={pos} draggable="true"/>);
		div.ondragstart = resizerDragStart;
		div.ondragover = defaulDragHover
	}

}

function resizerDragStart(this: HTMLDivElement, ev: DragEvent) {
	const popupable = findPopupableParent(this);
	popupable.manuallyResized = true;
	const toMove = popupable.eltForMove;
	const rect = toMove.getBoundingClientRect();
	//On fige :
	const st = toMove.style;
	st.margin = "unset"; //Peut perturber (margin: auto dans dialog).
	st.left = rect.left + 'px';
	st.top = rect.top + 'px';
	const dR = document.documentElement.clientWidth - rect.right;
	st.right = dR + 'px';
	const dB = document.documentElement.clientHeight - rect.bottom;
	st.bottom = dB + 'px';
	st.width = 'auto'; //écrase la valeur fit-content dans dialog
	st.height = 'auto'; //écrase la valeur fit-content dans dialog
	ev.dataTransfer.effectAllowed = 'move';
	ev.dataTransfer.setDragImage(<span/>, 0, 0);
	const pos = this.getAttribute('id');
	const baseLeft = rect.left - ev.screenX;
	const baseTop = rect.top - ev.screenY;
	const baseRight = dR + ev.screenX;
	const baseBottom = dB + ev.screenY;
	this.ondrag = (ev: DragEvent) => {
		if (ev.screenX === 0) return;//bug chrome63 : au drop un event 'drag' avec tout à 0 est dispatché.
		if (pos.indexOf('l') >= 0) toMove.style.left = (baseLeft + ev.screenX) + 'px';
		if (pos.indexOf('t') >= 0) toMove.style.top = (baseTop + ev.screenY) + 'px';
		if (pos.indexOf('r') >= 0) toMove.style.right = (baseRight - ev.screenX) + 'px';
		if (pos.indexOf('b') >= 0) toMove.style.bottom = (baseBottom - ev.screenY) + 'px';
	};
}

customElements.define('c-window-resizer', WindowResizer);

REG.reg.registerSkin('c-window-resizer', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: grid;
		min-height: 0;
		min-width: 0;
		grid-template-columns: 7px 1fr 7px;
		grid-template-rows: 7px auto 1fr 7px;
		grid-template-areas: "tl t tr" "l title r" "l slot r" "bl b br";
		margin: -7px;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}

	slot {
		grid-area: slot;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
	}

	slot[name=title] {
		grid-area: title;
		overflow: hidden;
	}

	div {
		grid-area: attr(id);
	}

	#tl {
		cursor: se-resize;
	}

	#br {
		cursor: se-resize;
	}

	#t, #b {
		cursor: ns-resize;
	}

	#tr, #bl {
		cursor: ne-resize;
	}

	#l, #r {
		cursor: ew-resize;
	}
`);

export function findPopupableParent(from: Node, root?: Element): IPopupable {
	return DOMSH.findFlatParentElt(from, root, IS_Popupable);
}

/** Utilisé pour bloquer la fermeture du popup sur perte du focus et peremettre le debug. */
export const DEBUG_TOOLTIP = false; //false

REG.reg.registerSkin("standard-dialog", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

  #main {
	  flex: 1;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: column;
	  padding: 1em;
  }

	#footer {
		display: flex;
		min-height: fit-content;
		min-width: 0;
		align-items: center;
		justify-content: flex-end;
		border-top: 1px solid var(--border-color);
	}
`);
